import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Ticket, CalendarPlus, UserPlus } from "lucide-react";
import apiClient from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import logo from "@/assets/logo.png";

export default function BookingSuccess() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");

  const params = new URLSearchParams(window.location.search);
  const isGuest = params.get("guest") === "1";

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }
    const statusUrl = isGuest ? `/public/payments/status/${sessionId}` : `/payments/status/${sessionId}`;

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await apiClient.get(statusUrl);
        if (data.payment_status === "paid") {
          if (!isGuest && localStorage.getItem("token")) await refreshUser();
          setStatus("success");
          return;
        }
      } catch (e) { /* ignore */ }
      if (attempts < 6) setTimeout(poll, 2000);
      else setStatus("failed");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid-overlay flex items-center justify-center px-5">
      <div className="w-full max-w-md glass rounded-3xl p-10 text-center animate-in-up">
        <img src={logo} alt="logo" className="h-12 w-auto object-contain mx-auto mb-8" />
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin" style={{ color: "#00e5ff" }} />
            <h1 className="font-display text-2xl font-bold text-white mb-2">Confirming Payment</h1>
            <p className="text-slate-400">Hang tight while we lock in your session...</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto mb-6" style={{ color: "#00ff9d" }} />
            <h1 className="font-display text-2xl font-bold text-white mb-2">Booking Confirmed!</h1>
            {isGuest ? (
              <>
                <p className="text-slate-400 mb-8">Your station is reserved — a confirmation was sent to your email. See you on the grid!</p>
                <div className="space-y-3">
                  <button onClick={() => navigate("/book")} className="neon-btn w-full py-4 px-6 gap-3" data-testid="book-another"><CalendarPlus className="w-5 h-5 shrink-0" /> <span>Book Another Session</span></button>
                  <button onClick={() => navigate("/register")} className="neon-outline-btn w-full py-4 px-6 gap-3" data-testid="guest-create-account"><UserPlus className="w-5 h-5 shrink-0" /> <span>Create Account &amp; Earn Points</span></button>
                </div>
              </>
            ) : (
              <>
                <p className="text-slate-400 mb-8">Your gaming station is reserved. See you on the grid, driver.</p>
                <button onClick={() => navigate("/my-bookings")} className="neon-btn w-full py-4" data-testid="view-bookings"><Ticket className="w-5 h-5" /> View My Bookings</button>
              </>
            )}
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-6" style={{ color: "#ff2e97" }} />
            <h1 className="font-display text-2xl font-bold text-white mb-2">Payment Not Confirmed</h1>
            <p className="text-slate-400 mb-8">We couldn't confirm your payment yet. {isGuest ? "Please try booking again." : "Check My Bookings or try again."}</p>
            <button onClick={() => navigate(isGuest ? "/book" : "/my-bookings")} className="neon-outline-btn w-full py-4">{isGuest ? "Back to Booking" : "Go to My Bookings"}</button>
          </>
        )}
      </div>
    </div>
  );
}
